import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, CreditCard, Download, Pill } from 'lucide-react';
import ManagementPage from '../../Common/components/common/ManagementPage';
import DetailItem from '../../Common/components/common/DetailItem';
import DetailGrid from '../../Common/components/common/DetailGrid';
import { useToast } from '../../Common/context/ToastContext';
import {
  getAdminRegisteredPharmacies,
  getAdminPharmacySyncLogs,
  downloadAdminPharmacyExcel,
} from '../api/pharmacy';

function PharmacyManage() {
  const { showToast } = useToast();
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;

  const [selectedItem, setSelectedItem] = useState(null);
  const [lastSync, setLastSync] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncHistory, setSyncHistory] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [loading, setLoading] = useState(true);

  // 동기화 이력 조회
  const fetchSyncLogs = async (withSpinner = false) => {
    if (withSpinner) setIsSyncing(true);
    try {
      const data = await getAdminPharmacySyncLogs({ limit: 3 });
      const logs = data?.logs ?? [];

      const mapped = logs.map((log) => {
        const started = log.started_at || '';
        const finished = log.finished_at || '';
        const [startDatePart, startTimePart] = started.split(' ');
        const [finishDatePart, finishTimePart] = finished.split(' ');
        const date = (startDatePart || '').replace(/-/g, '.'); // 예: 2026.02.03
        const time = startTimePart || finishTimePart || '';     // 시작시간 우선, 없으면 완료시간
        const status = log.status === 'SUCCESS' ? '성공' : log.status || '-';
        return {
          id: log.id,
          date,
          time,
          // 현재 API에 type 필드가 없으므로 기본값을 '자동'으로 표시 (필요시 백엔드 필드와 매핑)
          type: '자동',
          status,
          total: Number(log.total_count ?? 0),
          new: Number(log.inserted_count ?? 0),
          closed: Number(log.inactivated_count ?? 0),
          // 오류 건수 필드가 없다면 error_message 존재 여부로 0/1 표시
          error: log.error_count != null ? Number(log.error_count) : (log.error_message ? 1 : 0),
        };
      });

      setSyncHistory(mapped);

      if (logs.length > 0) {
        const first = logs[0];
        const finished = first.finished_at || first.started_at || '';
        const [d, t] = (finished || '').split(' ');
        const dateStr = (d || '').replace(/-/g, '.');
        if (dateStr) setLastSync(`${dateStr} ${t || ''}`.trim());
      }
    } catch (e) {
      showToastRef.current(e.message || '동기화 이력을 불러오지 못했습니다.');
    } finally {
      if (withSpinner) setIsSyncing(false);
    }
  };

  // 등록된 약국 목록 조회 (/api/admin/pharmacy/registered)
  const fetchPharmacies = async () => {
    setLoading(true);
    try {
      const data = await getAdminRegisteredPharmacies({ page: 1, limit: 100 });
      const raw = data?.pharmacies ?? data?.rows ?? data?.list ?? (Array.isArray(data) ? data : []);
      const rows = raw.map((r, i) => ({
        ...r,
        // 공통 ID
        id: r.id ?? r.pharmacy_id ?? r.pharmacyId ?? `P-${i}`,
        // 약국 기본 정보
        pharmacy_id: r.pharmacy_id ?? r.pharmacyId ?? r.id ?? `P-${i}`,
        name: r.name ?? '',
        institution_code: r.institution_code ?? r.institutionCode ?? '',
        // 연락처/주소
        phone: r.phone ?? r.contact ?? '',
        address: r.address ?? r.location ?? '',
        business_no: r.business_no ?? r.businessNo ?? '',
        // 영업자 / 대표자명 (없으면 '-')
        driver: r.driver ?? r.driver_name ?? r.driverName ?? '',
        representative: r.representative ?? r.owner ?? r.boss_name ?? r.bossName ?? '',
        // 기타 표시용
        created_at: r.created_at ?? r.createdAt ?? '',
        // 관리자 페이지에서만 쓰는 보조 필드
        merchant: r.cp_name ?? r.merchant ?? '',
        lastMonthSales: r.last_month_sales ?? r.lastMonthSales ?? '-',
      }));
      setPharmacies(rows);
    } catch (e) {
      showToastRef.current(e.message || '약국 목록을 불러오지 못했습니다.');
      setPharmacies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSyncLogs(false);
    fetchPharmacies();
  }, []);

  const handlePayment = (item) => {
    if (!item) return;
    const code = item.institution_code || item.pharmacy_id || item.id || '';
    localStorage.setItem(
      'currentSelectedPharmacyForPayment',
      JSON.stringify({
        name: item.name || '',
        code,
      }),
    );
    const url = `${window.location.origin}/cp/sugi`;
    window.open(url, '_blank', 'noopener,noreferrer,width=380,height=500');
  };

  const handleSync = async () => {
    await fetchSyncLogs(true);
    showToastRef.current('데이터 동기화가 완료되었습니다.');
  };

  const handleExcelDownload = async () => {
    try {
      await downloadAdminPharmacyExcel({});
      showToastRef.current('약국 엑셀 파일이 다운로드되었습니다.');
    } catch (e) {
      showToastRef.current(e.message || '약국 엑셀 다운로드에 실패했습니다.');
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden bg-white">
      {/* 동기화 현황 섹션 */}
      <section className="shrink-0 flex flex-col py-5 px-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-[18px] font-bold text-[#191f28]">
              약국 데이터 동기화 현황
            </h2>
            {/* 마지막 동기화 시간 사용 시 주석 해제 */}
            {/* {lastSync && (
              <span className="text-[16px] text-[#8b95a1] font-mono">
                마지막 동기화: {lastSync}
              </span>
            )} */}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSync}
              disabled={isSyncing}
              className="p-2 bg-[#f2f4f6] rounded-lg hover:bg-[#e5e8eb] disabled:opacity-50"
            >
              <RefreshCw
                size={18}
                className={
                  isSyncing ? 'animate-spin text-[#0064ff]' : 'text-[#8b95a1]'
                }
              />
            </button>
            <button
              type="button"
              onClick={handleExcelDownload}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#191f28] text-white rounded-lg font-bold text-[14px] hover:bg-black transition-colors"
            >
              <Download size={14} /> 전체 엑셀다운로드
            </button>
          </div>
        </div>

        {/* 동기화 이력 표 */}
        <div className="bg-white rounded-2xl border border-[#e0e0e0] shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[14px]">
              <thead className="bg-[#f9fafb] text-[#8b95a1] font-bold text-[14px] border-b border-[#f2f4f6]">
                <tr>
                  <th className="px-4 py-3">날짜/시간</th>
                  <th className="px-4 py-3">유형</th>
                  <th className="px-4 py-3">상태</th>
                  <th className="px-4 py-3">전체 약국</th>
                  <th className="px-4 py-3">신규 영업</th>
                  <th className="px-4 py-3">폐업 처리</th>
                  <th className="px-4 py-3">동기화 오류</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#f2f4f6] [&>tr:last-child>td]:border-b [&>tr:last-child>td]:border-[#f2f4f6]">
                {!syncHistory?.length ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-[15px] font-medium text-[#8b95a1]">
                      데이터가 없습니다.
                    </td>
                  </tr>
                ) : (
                  syncHistory.slice(0, 3).map((history) => (
                  <tr
                    key={history.id}
                    className="hover:bg-[#f9fafb] transition-all"
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="font-medium text-[#191f28]">
                        {history.date}
                      </span>
                      <span className="text-[14px] text-[#8b95a1] font-mono ml-1">
                        {history.time}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2.5 py-0.5 rounded-lg text-[13px] font-bold ${
                          history.type === '자동'
                            ? 'bg-[#e7f5ff] text-[#0064ff]'
                            : 'bg-[#f2f4f6] text-[#4e5968]'
                        }`}
                      >
                        {history.type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2.5 py-0.5 rounded-lg text-[13px] font-bold bg-[#e7f5ff] text-[#0064ff]">
                        {history.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-bold text-[#191f28]">
                      {history.total?.toLocaleString?.() ?? history.total}곳
                    </td>
                    <td className="px-4 py-3 font-bold text-[#0064ff]">
                      +{history.new}곳
                    </td>
                    <td className="px-4 py-3 font-bold text-[#f04452]">
                      -{history.closed}곳
                    </td>
                    <td className="px-4 py-3 font-bold text-[#adb5bd]">
                      {history.error}건
                    </td>
                  </tr>
                ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 약국 목록 + 상세: 동기화 아래 남는 높이까지 채움 */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <ManagementPage
          title="약국관리"
          emptyStateIcon={<Pill size={32} className="opacity-20" />}
          hideSearch={false}
          hideExcelButton
          leftActions={
            <div className="text-[16px] font-bold text-[#191f28]">
              <span className="text-[#0064ff]">{pharmacies.length}곳</span>
            </div>
          }
          tableHeaders={
            <>
              <th className="px-4 py-3">약국명</th>
              <th className="px-4 py-3">영업자</th>
              <th className="px-4 py-3">대표자명</th>
              <th className="px-4 py-3">연락처</th>
            </>
          }
          data={pharmacies}
          renderRow={(item) => (
            <>
              <td className="px-4 py-3 font-bold text-[#191f28]">{item.name || '-'}</td>
              <td className="px-4 py-3 font-medium text-[#4e5968]">{item.driver || '-'}</td>
              <td className="px-4 py-3 font-medium text-[#4e5968]">{item.representative || '-'}</td>
              <td className="px-4 py-3 font-mono text-[#4e5968]">{item.phone || '-'}</td>
            </>
          )}
          detailTitle="약국 상세정보"
          detailButtonLabel="결제"
          detailButtonIcon={<CreditCard size={14} />}
          detailButtonSmall
          detailButtonDisabled={!selectedItem}
          onDetailButtonClick={() => handlePayment(selectedItem)}
          onExcelDownload={handleExcelDownload}
          excelButtonClassName="inline-flex items-center gap-2 px-3 py-1.5 bg-[#191f28] text-white rounded-lg font-bold text-[14px] hover:bg-black transition-colors"
          selectedItem={selectedItem}
          onSelectItem={setSelectedItem}
          renderDetail={(item) => (
            <>
              <DetailGrid>
                <DetailItem label="약국 ID" value={item.pharmacy_id ?? item.id ?? '-'} />
                <DetailItem label="대표자명" value={item.representative || '-'} />
                <DetailItem label="약국명" value={item.name || '-'} />
                <DetailItem label="약국코드" value={item.institution_code || '-'} />
                <DetailItem label="사업자등록번호" value={item.business_no || '-'} />
                <DetailItem label="연락처" value={item.phone || '-'} />
                <DetailItem label="영업자" value={item.driver || '-'} />
                <DetailItem label="등록일" value={item.created_at || '-'} />
              </DetailGrid>
              <div className="flex justify-between items-center gap-4 min-w-0 mt-[1.5rem]">
                <span className="text-[13px] font-bold text-[#8b95a1] shrink-0">주소</span>
                <span
                  className="text-[16px] font-bold text-[#191f28] text-right whitespace-nowrap overflow-hidden text-ellipsis min-w-0 flex-1"
                  title={item.address || item.location || ''}
                >
                  {item.address || item.location || '-'}
                </span>
              </div>
            </>
          )}
        />
      </div>
    </div>
  );
}

export default PharmacyManage;
